import React, { useContext, useEffect, useState } from "react";
import {
  Button, Card, CardBody, Badge, Input, UncontrolledPopover,
  PopoverHeader,
  PopoverBody,
} from "reactstrap";
import Select, { components } from "react-select";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { useDispatch, useSelector } from "react-redux";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { traineeLetter } from "constants/trainee";
import { downloadLetterQR, downloadLetter } from "action/letter";
import { getTraineeLetter } from "action/trainee";
import CommonModal from "components/CommonComponents/CommonModal";
import { LETTER_SCOPE_OPTIONS, LETTER_TYPE_OPTIONS, TRAINEE_LETTER_STATUS, getStatusColor, LETTERS_TYPE_OPTIONS } from "components/constant";
import { fixedWidthCustomStyle } from "constants/CommonConstants";


function TraineeLetterListing({ userID }) {
  const { notify } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const letterDataList = useSelector((state) => state.trainees.traineeLetter);

  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    letterType: "",
    letterScope: "",
    referenceNo: "",
    issuedToCustomer: "",
    project: "",
    status: null,
    filters: [],
  });

  const [alternative, setAlternative] = useState({
    isLoading: true,
    showLoaderOnClick: false,
    error: "",
    noRecords: false,
  });

  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    fileUrl: "",
    status: ""
  });
  const [isDownloading, setIsDownloading] = React.useState(false);

  const buildFilterParams = () => {
    const filters = [];

    if (filterState.letterType) {
      filters.push({
        field: "LetterType",
        condition: "eq",
        filterValues: [filterState.letterType.value],
      });
    }

    if (filterState.letterScope) {
      filters.push({
        field: "LetterScope",
        condition: "eq",
        filterValues: [filterState.letterScope.value],
      });
    }

    if (filterState.referenceNo.trim()) {
      filters.push({
        field: "ReferenceNo",
        condition: "like",
        filterValues: [filterState.referenceNo.trim()],
      });
    }

    if (filterState.issuedToCustomer.trim()) {
      filters.push({
        field: "IssuedToCustomer",
        condition: "like",
        filterValues: [filterState.issuedToCustomer.trim()],
      });
    }

    if (filterState.project.trim()) {
      filters.push({
        field: "Project",
        condition: "like",
        filterValues: [filterState.project.trim()],
      });
    }

    if (filterState.status) {
      filters.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.status.value],
      });
    }

    return {
      filters: filters.length > 0 ? JSON.stringify(filters) : [],
    };
  };

  const formattedList = letterDataList?.data?.Letter?.map((item, index) => {
    return {
      ...item,
      srNo:
        (pageNum - 1) *
        (letterDataList?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    };
  });

  useEffect(() => {
    if (!userID) return;

    getTraineeLetter(dispatch, pageNum, buildFilterParams(), userID);

    return () => {
      dispatch({ type: traineeLetter, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filters, userID]);

  useEffect(() => {
    if (letterDataList?.code === 200) {
      setAlternative({
        isLoading: false,
        error: "",
        noRecords: letterDataList?.data?.Letter?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (letterDataList?.code || letterDataList?.message) {
      setAlternative({
        isLoading: false,
        error:
          letterDataList?.message ||
          "Failed to load letters list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [letterDataList]);

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 60,
      maxWidth: 70,
      centerAlign: true,

    },
    {
      Header: "Letter Type",
      accessor: "letterType",
      Cell: ({ value }) => <span>{LETTERS_TYPE_OPTIONS.find(option => option?.value === value)?.label || "-"}</span>,
    },
    {
      Header: "Letter scope",
      accessor: "letterScope",
      Cell: ({ value }) => <span>{LETTER_SCOPE_OPTIONS.find(option => option?.value === value)?.label || "-"}</span>,
    },
    {
      Header: "Reference No",
      accessor: "referenceNo",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    // {
    //   Header: "project",
    //   accessor: "project",
    //   Cell: ({ value }) => <span>{value || "-"}</span>,
    // },
    {
      Header: "issued on",
      accessor: "issueDate",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "valid Till",
      accessor: "validTill",
    },
    {
      Header: "Other Information",
      accessor: "otherInfo",
      disableSortBy: true,
      Cell: ({ row }) => {
        const data = row.original;
        const id = data.ID;

        return (
          <>
            {/* Styled Button */}
            <Button
              size="sm"
              color="link"
              id={`otherInfo_${id}`}
              className="p-0"
            >
              <i className="fa fa-info-circle mr-1" />
              Details
            </Button>

            <UncontrolledPopover
              placement="right"
              target={`otherInfo_${id}`}
              trigger="hover focus"
            >
              <PopoverHeader className="text-center">
                Letter Details
              </PopoverHeader>

              <PopoverBody className="custom-popover-body">
                {data?.letterScope !== "INDIVIDUAL" && (
                  <>
                    <div className="d-flex mb-2">
                      <div className="w-50">
                        <small className="text-muted">Project</small>
                        <div className="font-weight-bold">
                          {data.project || "-"}
                        </div>
                      </div>

                      <div className="w-50">
                        <small className="text-muted">Issued To Customer</small>
                        <div className="font-weight-bold">
                          {data.issuedToCustomer || "-"}
                        </div>
                      </div>
                    </div>

                    <hr className="my-2" />
                  </>
                )}

                {data.letterType === "TOOL_LETTER" ? (
                  <div className="d-flex mb-2">
                    <div className="w-50">
                      <small className="text-muted">From Location</small>
                      <div className="font-weight-bold">
                        {data.fromLocation || "-"}
                      </div>
                    </div>

                    <div className="w-50">
                      <small className="text-muted">To Location</small>
                      <div className="font-weight-bold">
                        {data.toLocation || "-"}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex mb-2">
                    <div className="w-50">
                      <small className="text-muted">Request Of</small>
                      <div className="font-weight-bold">
                        {data.requestOf || "-"}
                      </div>
                    </div>

                    <div className="w-50">
                      <small className="text-muted">Created At</small>
                      <div className="font-weight-bold">
                        {data.createdAt || "-"}
                      </div>
                    </div>
                  </div>
                )}

                {/* <hr className="my-2" />

                <div className="d-flex mb-2">
                  <div className="w-50">
                    <small className="text-muted">Issued On</small>
                    <div className="font-weight-bold">
                      {data.issueDate || "-"}
                    </div>
                  </div>

                  <div className="w-50">
                    <small className="text-muted">Valid Till</small>
                    <div className="font-weight-bold">
                      {data.validTill || "-"}
                    </div>
                  </div>
                </div> */}

                <hr className="my-2" />

                <div className="d-flex mb-2">
                  <div className="w-50">
                    <small className="text-muted">Created By</small>
                    <div className="font-weight-bold">
                      {data.createdByName || "-"}
                    </div>
                  </div>

                  {data.approvedBy > 0 && <div className="w-50">
                    <small className="text-muted">{data.status === "APPROVED" ? "Approved By" : data.status === "REJECTED" ? "Rejected By" : "Approval Action By"}</small>
                    <div className="font-weight-bold">
                      {data.approvedByName || "-"}
                    </div>
                  </div>}
                </div>
              </PopoverBody>
            </UncontrolledPopover>
          </>
        );
      },
    },
    {
      Header: "Status",
      accessor: "status",
      centerAlign: true,
      Cell: ({ value }) => (
        <Badge color={value === "ISSUED" ? "success" : "warning"} pill>
          {value}
        </Badge>
      ),
    },
    {
      Header: "View Letter",
      accessor: "referenceNoPath",
      disableSortBy: true,
      Cell: ({ value, row }) => {
        const id = row.original.ID;
        return value ? (
          <div className="actions-right d-flex justify-content-start">
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`download${id}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                e.stopPropagation();
                handlePreviewLetter(row.original.referenceNo, row.original.status);
              }}
            >
              <i className="fa fa-eye mr-1"></i>
              <span>Preview</span>
            </Button>
          </div>
        ) : (
          "-"
        );
      },
    },
    {
      Header: "QR Code",
      accessor: "documentQrPath",
      disableSortBy: true,
      Cell: ({ value, row }) => {
        const id = row.original.ID;
        return value ? (
          <div className="actions-right d-flex justify-content-start">
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`download${id}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                e.stopPropagation();
                handleDownloadQR(row.original.encodedReferenceNo);
              }}
            >
              <i class="fa-solid fa-download"></i>
              <span>Download</span>
            </Button>
          </div>
        ) : (
          "-"
        );
      },
    },
  ];

  const handleDownloadQR = async (ID) => {
    setIsDownloading(true);
    try {
      const res = await downloadLetterQR(ID);
      if (res.status === 200 && res.data) {
        const fileName =
          res.headers["content-disposition"]?.match(/filename="(.+?)"/)?.[1] ||
          "qr-code.png";

        const url = URL.createObjectURL(res.data);

        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      } else if (res.code === 404) {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message ||
          "Failed to download authorization letter QR code. Please try again.";
        notify("danger", message);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message ||
          "Failed to download authorization letter QR code. Please try again.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        "Failed to download authorization letter QR code. Please try again.",
      );
    }
    setIsDownloading(false);
  };

  const handlePreviewLetter = async (ID, status) => {
    setIsDownloading(true);
    try {
      const res = await downloadLetter(ID);
      if (res.status === 200 && res.data) {
        // const fileName =
        //   res.headers["content-disposition"]?.match(/filename="(.+?)"/)?.[1] ||
        //   "authorization-letter.pdf";
        const url = URL.createObjectURL(res.data);

        //set for preview
        setPreviewModal({ isOpen: true, fileUrl: url, status: status });
        // const a = document.createElement("a");
        // a.href = url;
        // a.download = fileName;
        // a.click();
        // URL.revokeObjectURL(url);
      } else if (res.code === 404) {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message ||
          "Failed to download authorization letter. Please try again.";
        notify("danger", message);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message ||
          "Failed to download authorization letter. Please try again.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        "Failed to download authorization letter. Please try again.",
      );
    }
    setIsDownloading(false);
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={getStatusColor(props?.data?.value)}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={getStatusColor(props?.data?.value)}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const clearSearch = () => {
    setFilterState({
      letterType: "",
      letterScope: "",
      referenceNo: "",
      issuedToCustomer: "",
      project: "",
      status: null,
      filters: [],
    });
    
    setAlternative((prev) => ({
      ...prev,
      isLoading: true,
    }));

    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newFilters = buildFilterParams();

    if (!newFilters.filters.length) return;

    if (JSON.stringify(filterState.filters) !== JSON.stringify(newFilters)) {
      setAlternative((prev) => ({
        ...prev,
        showLoaderOnClick: true,
        isLoading: true,
      }));

      setFilterState((prev) => ({
        ...prev,
        filters: newFilters,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  return (
    <>
      <Card className="mb-6">
        {/*<CardHeader>
             <h4 className="mb-0">Enrolled Courses</h4> 
          </CardHeader>*/}
        <CardBody className="p-4">
          {isDownloading && (
            <div className="logout-overlay">
              <div className="logout-card">
                <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
                <div className="font-weight-bold text-dark">
                  Loading… Please wait.
                </div>
              </div>
            </div>
          )}

          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
            {/* Left side - Filter Inputs + Search */}
            <div className="d-flex flex-wrap align-items-center filter-group">
              <Select
                options={LETTER_TYPE_OPTIONS}
                styles={fixedWidthCustomStyle("15rem")}
                className="mr-2 mb-2 filter-input-large"
                placeholder="Letter type"
                value={filterState.letterType}
                onKeyDown={handleKeyDown}
                onChange={(selected) =>
                  setFilterState((prev) => ({
                    ...prev,
                    letterType: selected,
                  }))
                }
              />

              <Select
                options={LETTER_SCOPE_OPTIONS}
                styles={fixedWidthCustomStyle("15rem")}
                onKeyDown={handleKeyDown}
                className="mr-2 mb-2 filter-input-large"
                placeholder="Letter scope"
                value={filterState.letterScope}
                onChange={(selected) =>
                  setFilterState((prev) => ({
                    ...prev,
                    letterScope: selected,
                  }))
                }
              />

              <Input
                className="mr-2 mb-2 filter-input-large"
                placeholder="Search by reference no"
                value={filterState.referenceNo}
                onKeyDown={handleKeyDown}
                onChange={(e) =>
                  setFilterState((prev) => ({
                    ...prev,
                    referenceNo: e.target.value,
                  }))
                }
              />

              {/* <Input
                className="mr-2 mb-2 filter-input-large"
                placeholder="Search by customer"
                value={filterState.issuedToCustomer}
                onKeyDown={handleKeyDown}
                onChange={(e) =>
                  setFilterState((prev) => ({
                    ...prev,
                    issuedToCustomer: e.target.value,
                  }))
                }
              />

              <Input
                className="mr-2 mb-2 filter-input-large"
                placeholder="Search by project"
                value={filterState.project}
                onKeyDown={handleKeyDown}
                onChange={(e) =>
                  setFilterState((prev) => ({
                    ...prev,
                    project: e.target.value,
                  }))
                }
              /> */}

              <Select
                placeholder="Status"
                styles={fixedWidthCustomStyle("15rem")}
                className="mr-2 mb-2 filter-input-large"
                onKeyDown={handleKeyDown}
                options={TRAINEE_LETTER_STATUS}
                value={filterState.status}
                onChange={(selected) =>
                  setFilterState((prev) => ({
                    ...prev,
                    status: selected,
                  }))
                }
                components={{ Option, SingleValue }}
              />

              <Button
                color="default"
                className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${alternative.showLoaderOnClick ? "search-btn" : ""}`}
                onClick={handleSearch}
              >
                {alternative.showLoaderOnClick ? (
                  <i className="fa fa-spinner fa-spin" />
                ) : (
                  <>
                    <i className="fa fa-search mr-1" />
                    Search
                  </>
                )}
              </Button>

              {filterState.filters?.filters?.length > 0 && (
                <Button
                  color="default"
                  className="d-flex align-items-start mb-2 ml-0 filter-btn btn-deep-blue px-4"
                  onClick={clearSearch}
                >
                  <i className="fa fa-times mr-1 mt-1" /> Clear
                </Button>
              )}
            </div>
          </div>

          <div className="mt-2 mb-3 mx-1">
            {letterDataList?.data?.totalRecords ? (
              <>
                {filterState.filters?.filters?.length > 0 ? (
                  <span className="total-records">
                    Showing {letterDataList?.data?.totalFilteredRecords} of{" "}
                    {letterDataList?.data?.totalRecords} Records
                  </span>
                ) : (
                  <span className="total-records">
                    Total Records : {letterDataList?.data?.totalRecords}
                  </span>
                )}
              </>
            ) : null}
          </div>

          {alternative.isLoading ? (
            <div className="loaderContainer-sm">
              <i className="fa fa-spinner fa-spin fa-2xl loader" />
            </div>
          ) : alternative.error ? (
            <>
              <div className="loaderContainer-sm error-style">
                <div className="error-icon">
                  <i class="fa-solid fa-circle-exclamation"></i>
                </div>
                <span>{alternative.error}</span>
              </div>
            </>
          ) : alternative.noRecords ? (
            <div className="loaderContainer-sm empty-style">
              <div className="empty-icon">
                <i class="fa-solid fa-circle-info"></i>
              </div>
              <span>No letters found.</span>
            </div>
          ) : (
            <div className="table-responsive">
              <CommonTable
                columns={columns}
                data={formattedList || []}
                defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                activePage={pageNum}
                setPageNum={setPageNum}
                totalPages={Math.ceil(
                  letterDataList?.data?.totalRecords /
                  letterDataList?.data?.noOfRecordsPerPage ||
                  DEFAULT_RECORDS_PER_PAGE,
                )}
                showPaginationBottom={true}
              />
            </div>
          )}
        </CardBody>
      </Card>

      <CommonModal
        isOpen={previewModal.isOpen}
        toggle={() => setPreviewModal({ isOpen: false, fileUrl: "", status: "" })}
        title="Letter Preview"
        size="xl"
        height="80vh"
      >
        {previewModal.fileUrl && (
          <iframe
            src={
              previewModal.status !== "ISSUED"
                ? `${previewModal.fileUrl}#toolbar=0&navpanes=0&scrollbar=0`
                : previewModal.fileUrl
            }
            title="PDF Preview"
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        )}
      </CommonModal>
    </>
  );
}

export default TraineeLetterListing;
